/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/DoUntilSuccess.ts
 * @create:      2025-07-01 13:21:18.865
 * @modify:      2025-07-11 21:19:12.920
 * @version:     6.0.0
 * @times:       10
 * @lines:       76
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { Sleep } from "./Promise";

/**
 * Repeatedly executes an asynchronous action until it succeeds or the maximum number of retries is reached.
 *
 * @param action - An asynchronous function that returns a boolean indicating success (`true`) or failure (`false`).
 * @param retryDelayInMillionSeconds - The delay in milliseconds between retries. Defaults to 500 ms.
 * @param retryCount - The maximum number of retry attempts. Defaults to 5.
 * @param startImmediately - If `true`, the action is executed immediately before any delay. Defaults to `false`.
 * @returns A promise that resolves to `true` if the action succeeds within the allowed attempts, or `false` otherwise.
 * 
 * @remarks 
 * - The `action` will be executed at least once.
 * - If the function returns "false", the actual execution count should be `1 + retryCount`
 * 
 * @since 1.1.5
 * @version 2025-07-10
 */
export async function DoUntilSuccess(action: () => Promise<boolean>, retryDelayInMillionSeconds = 500, retryCount = 5, startImmediately = false) {
    if (startImmediately) {
        if (await action()) {
            return true;
        }
    } else {
        await Sleep(retryDelayInMillionSeconds);

        if (await action()) {
            return true;
        }
    }

    while (retryCount > 0) {
        await Sleep(retryDelayInMillionSeconds);

        if (await action()) {
            return true;
        }

        retryCount--;
    }

    return false;
}
